import { memo, useMemo } from "react"
import dayjs from "dayjs"
import { cn } from "@/shared/utils/general/cn"
import { StaticIcon } from "@/shared/icons/StaticIcon"

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface CalendarDayCellProps {
  date: dayjs.Dayjs
  isToday: boolean
  isSelected: boolean
  isPast: boolean
  isDisabled: boolean
  isCurrentMonth: boolean // Only used for edge-of-month fading
  /** Number of selected tasks scheduled on this date (multi-select highlight) */
  highlightCount?: number
  onSelect: (date: dayjs.Dayjs) => void
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * Single day cell in the InfiniteCalendar grid.
 *
 * States:
 * - isToday: ring accent highlight
 * - isSelected: filled primary background
 * - isPast: subtle opacity reduction
 * - isDisabled: not clickable, very low opacity
 */
export const CalendarDayCell = memo(function CalendarDayCell({
  date,
  isToday,
  isSelected,
  isPast,
  isDisabled,
  isCurrentMonth,
  highlightCount = 0,
  onSelect,
}: CalendarDayCellProps) {
  const dayNumber = useMemo(() => date.date(), [date])
  const isHighlighted = highlightCount > 0 && !isSelected

  return (
    <button
      type="button"
      disabled={isDisabled}
      onClick={() => {
        if (!isDisabled) onSelect(date)
      }}
      className={cn(
        "group relative flex h-11 w-full items-center justify-center text-sm",
        "transition-colors duration-100",
        // Disabled
        isDisabled && "cursor-default opacity-20",
        // Past but not disabled
        !isDisabled && isPast && !isSelected && "opacity-60",
        // Normal state
        !isDisabled &&
          !isSelected &&
          isCurrentMonth &&
          "text-foreground",
        // Out of current month view
        !isDisabled &&
          !isSelected &&
          !isCurrentMonth &&
          "text-muted-foreground/50",
        // Today (not selected)
        isToday && !isSelected && "font-bold text-primary",
        // Highlighted (multi-select: tasks scheduled on this date)
        isHighlighted && "font-semibold text-primary",
        // Selected — rounded circle
        isSelected && "font-semibold",
      )}
    >
      {/* Hover: subtle circle matching the ring pattern */}
      {!isDisabled && !isSelected && !isHighlighted && (
        <span className={cn(
          "absolute size-9 rounded-full transition-colors duration-100",
          isCurrentMonth
            ? "group-hover:bg-muted"
            : "group-hover:bg-muted/50",
        )} />
      )}
      {/* Selected: perfect circle ring (v1 style — outline, not filled) */}
      {isSelected && (
        <span className="absolute size-9 rounded-full ring-[3px] ring-primary" />
      )}
      {/* Highlighted: ring for dates with selected tasks (v1 multi-select) */}
      {isHighlighted && (
        <span className="absolute size-9 rounded-full ring-[3px] ring-primary" />
      )}
      {/* Badge count for multi-select (2+) */}
      {highlightCount > 1 && !isSelected && (
        <span className="absolute right-1.5 top-0.5 z-20 flex size-3.5 items-center justify-center rounded-full bg-primary text-[8px] font-bold leading-none text-primary-foreground">
          {highlightCount}
        </span>
      )}
      {/* Today indicator ring (when not selected and not highlighted) */}
      {isToday && !isSelected && !isHighlighted && (
        <span className="absolute size-9 rounded-full ring-[3px] ring-primary/30" />
      )}
      {/* Today: star icon. Other days: number */}
      <span className={cn("relative z-10", isSelected && "text-primary")}>
        {isToday ? (
          <StaticIcon
            type="today"
            active
            size={20}
            mode="color"
          />
        ) : (
          dayNumber
        )}
      </span>
    </button>
  )
})
